import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { MdOutlineFileDownload, MdWarningAmber } from "react-icons/md";
import * as expenseService from "../../services/expenseService";
import SlidePanel from "../../components/SlidePanel/SlidePanel";
import Loader from "../../components/Loader/Loader";
import { formatDateHyphenated } from "../../utils/dateFormat";
import { parseExpenseFile, toApiRow, buildTemplateWorkbook } from "./expenseSheetParser";
import { STANDARD, BLUE_DREAM, JC_ALQURE, TEMPLATE_HEADERS, TEMPLATE_COLUMNS, resolveTemplate } from "./expenseTemplates";
import CustomerPicker from "./CustomerPicker";
import InvoiceDateField from "./InvoiceDateField";
import { thStyle, tdStyle, cardStyle, labelStyle, badgeStyle, describeServices, todayYmd } from "./expenseStyles";

const TEMPLATE_FILE_NAME = {
  [STANDARD]: "expense_upload_template.xlsx",
  [BLUE_DREAM]: "blue_dream_expense_template.xlsx",
  [JC_ALQURE]: "job_card_expense_template.xlsx",
};

const downloadTemplate = (template) => {
  XLSX.writeFile(buildTemplateWorkbook(template), TEMPLATE_FILE_NAME[template], { cellDates: true });
};

// First preview column: the template's own serial-number header (STANDARD has none, so it shows the Excel row).
const SERIAL_HEADER = { [STANDARD]: "#", [BLUE_DREAM]: "SR NO", [JC_ALQURE]: "S No" };

// Help bullets under "Expected columns". STANDARD's wording is the original.
const TEMPLATE_HINTS = {
  [STANDARD]: [
    "WASHING, POLISHING, PDI and VALETING are Yes / No. Several can be Yes on one row.",
    "Use real dates formatted dd-mmm-yyyy, e.g. 01-Sep-2026. Numeric text dates are read as DD/MM/YYYY. VAT defaults to 5% when blank.",
    "VAT amount and totals are always recalculated from NET AMOUNT and VAT.",
  ],
  [BLUE_DREAM]: [
    "AMOUNT is the price before VAT. NET AMOUNT (including VAT) is only checked against the recalculated total. SR NO is ignored.",
    "Dates are always read day first: 01.09.2026, 01/09/2026 or 01-Sep-2026 all mean 1 September. VAT defaults to 5% when blank.",
    "A row is a duplicate only when every column matches a saved job (or an earlier row of the file).",
  ],
  [JC_ALQURE]: [
    "Description is required. The Vehicle number (plate) is stored as the vehicle identifier. Qty defaults to 1 when blank.",
    "Amount before VAT = Price x Qty; VAT and Total are recalculated. VAT defaults to 5% when blank.",
    "Dates are always read day first (01/09/2026, 01.09.2026, 1-Sep-26). A row is a duplicate only when every column matches a saved job (or an earlier row of the file).",
  ],
};

const DUP_LABEL = {
  EXISTING: "Duplicate - already saved",
  IN_FILE: "Duplicate - repeated in file",
};

// Two steps in one drawer: (1) pick the file, which parses client-side and
// asks the API which rows are duplicates, then (2) a preview where the user
// picks the rows to keep and whether to invoice straight away (for which the
// customer must be chosen). The branch is fixed by the page it is opened from.
function UploadExpenseDrawer({ show, onHide, centerId, branchName, template: templateProp, onUploaded }) {
  const template = resolveTemplate(templateProp);
  const templateColumns = TEMPLATE_COLUMNS[template] || null; // null = STANDARD's original columns
  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState(null); // null = step 1, array = preview
  const [dupReason, setDupReason] = useState({}); // row index -> EXISTING | IN_FILE
  const [dupKeys, setDupKeys] = useState({}); // row index -> the API's duplicate key, echoed back on upload
  const [selected, setSelected] = useState(() => new Set());
  const [generateNow, setGenerateNow] = useState(false);
  const [customer, setCustomer] = useState(null); // Bill To, required when generateNow
  const [invoiceDate, setInvoiceDate] = useState(todayYmd); // past or future allowed
  const [parsing, setParsing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!show) return;
    setFileName("");
    setRows(null);
    setDupReason({});
    setDupKeys({});
    setSelected(new Set());
    setGenerateNow(false);
    setCustomer(null);
    setInvoiceDate(todayYmd());
    setError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!centerId) {
      setError("This branch is not active, so jobs can't be uploaded.");
      return;
    }
    setError("");
    setParsing(true);
    try {
      const parsed = await parseExpenseFile(file, template);
      if (parsed.error) {
        setError(parsed.error);
        return;
      }
      const valid = parsed.rows.map((r, index) => ({ r, index })).filter(({ r }) => r.errors.length === 0);
      const reasons = {};
      const keys = {};
      if (valid.length) {
        // The API compares every uploaded column, so it gets the same rows the upload will send.
        const res = await expenseService.checkDuplicates({
          expense_center_id: centerId,
          rows: valid.map(({ r }) => toApiRow(r, template)),
        });
        if (!res?.success) {
          setError(expenseService.responseMessage(res, "Could not check for duplicates"));
          return;
        }
        (res.data?.duplicates || []).forEach((d) => {
          reasons[valid[d.index].index] = d.reason;
          keys[valid[d.index].index] = d.key;
        });
      }
      setFileName(file.name);
      setRows(parsed.rows);
      setDupReason(reasons);
      setDupKeys(keys);
      // Errors and duplicates start unchecked - duplicates are opt-in.
      setSelected(new Set(parsed.rows.map((r, i) => i).filter((i) => parsed.rows[i].errors.length === 0 && !reasons[i])));
    } catch (err) {
      setError(err.message || "Could not read that file. Upload the .xlsx template.");
    } finally {
      setParsing(false);
    }
  };

  const counts = useMemo(() => {
    if (!rows) return null;
    const errors = rows.filter((r) => r.errors.length).length;
    const dups = Object.keys(dupReason).length;
    // Range of the job dates in the file: a whole file that landed in the wrong
    // month (e.g. 09-Jan instead of 01-Sep) is obvious here at a glance.
    const dates = rows.map((r) => r.job_schedule_date).filter(Boolean).sort();
    return {
      total: rows.length,
      errors,
      dups,
      ready: rows.length - errors - dups,
      firstDate: dates[0] || null,
      lastDate: dates[dates.length - 1] || null,
    };
  }, [rows, dupReason]);

  const selectableIdx = rows ? rows.map((r, i) => i).filter((i) => rows[i].errors.length === 0) : [];
  const allSelected = selectableIdx.length > 0 && selectableIdx.every((i) => selected.has(i));

  const toggleRow = (i) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(selectableIdx));

  const handleConfirm = async () => {
    const chosen = [...selected].sort((a, b) => a - b).map((i) => ({ row: rows[i], index: i }));
    if (!chosen.length) {
      toast.error("Select at least one row to upload");
      return;
    }
    if (generateNow && (!customer || !invoiceDate)) {
      toast.error(customer ? "Choose the invoice date" : "Select the customer this invoice is for");
      return;
    }
    setSubmitting(true);
    try {
      const res = await expenseService.uploadEntries({
        expense_center_id: centerId,
        rows: chosen.map(({ row }) => toApiRow(row, template)),
        included_duplicate_keys: chosen.filter(({ index }) => dupReason[index]).map(({ index }) => dupKeys[index]),
        file_name: fileName,
        total_rows_parsed: rows.length,
        generate_invoice_on_upload: generateNow,
      });
      if (!res?.success) {
        toast.error(expenseService.responseMessage(res, "Upload failed"));
        return;
      }
      const { summary, inserted_entry_ids: ids, batch_id: batchId } = res.data;
      const skipped = summary.rows_skipped_duplicate ? `, ${summary.rows_skipped_duplicate} duplicate(s) skipped` : "";
      toast.success(`${summary.rows_inserted} expense entries uploaded${skipped}`);

      if (generateNow) {
        try {
          await expenseService.generateInvoice(ids, batchId, customer, invoiceDate);
          toast.success("Invoice generated and downloaded");
        } catch (err) {
          toast.error(`Entries were uploaded, but the invoice could not be generated: ${expenseService.errorMessage(err)}`);
        }
      }
      onUploaded();
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    if (!submitting && !parsing) onHide();
  };

  const mismatchIcon = (r) =>
    r.totalMismatch !== null && (
      <span
        title={`The sheet says ${r.totalMismatch}. The total is recalculated from ${template === JC_ALQURE ? "Price, Qty" : template === BLUE_DREAM ? "Amount" : "Net Amount"} and VAT.`}
        style={{ marginLeft: 6, color: "#B45309" }}
      >
        <MdWarningAmber size={15} />
      </span>
    );

  const footer = rows ? (
    <>
      <button className="btn btn-outline-secondary px-4" onClick={() => setRows(null)} disabled={submitting}>
        Back
      </button>
      <button
        className="btn Button primaryBackground text-white px-4"
        onClick={handleConfirm}
        disabled={submitting || selected.size === 0 || (generateNow && (!customer || !invoiceDate))}
      >
        {submitting ? (
          <Loader style={{ width: 40, height: 20 }} />
        ) : generateNow ? (
          `Upload ${selected.size} & Generate Invoice`
        ) : (
          `Upload ${selected.size} Row${selected.size === 1 ? "" : "s"}`
        )}
      </button>
    </>
  ) : (
    <button className="btn btn-outline-secondary px-4" onClick={close}>
      Cancel
    </button>
  );

  return (
    <SlidePanel show={show} onHide={close} title="Upload Expense Spreadsheet" footer={footer} width={980}>
      {!rows ? (
        <div>
          <div className="mb-3" style={{ fontSize: "14px", color: "#374151" }}>
            Uploading to <strong>{branchName || "this branch"}</strong>
            {!centerId && <div style={{ fontSize: "12px", color: "#B45309", marginTop: 6 }}>This branch is not active. Activate it under Branch Jobs &gt; Setup.</div>}
          </div>

          <div className="mb-3">
            <label style={labelStyle}>Excel file (.xlsx / .xls) *</label>
            <input
              type="file"
              accept=".xlsx,.xls"
              aria-label="Expense spreadsheet"
              className="form-control"
              style={{ fontSize: "14px" }}
              onChange={onFileChange}
              disabled={parsing || !centerId}
            />
            {parsing && (
              <div className="d-flex align-items-center gap-2 mt-2" style={{ fontSize: "13px", color: "#6B7280" }}>
                <Loader style={{ width: 30, height: 16 }} /> Reading file and checking for duplicates...
              </div>
            )}
          </div>

          {error && (
            <div className="alert alert-danger py-2" style={{ fontSize: "13px" }}>
              {error}
            </div>
          )}

          <div style={{ ...cardStyle, padding: "16px", fontSize: "13px", color: "#374151" }}>
            <div className="fw-bold mb-2">Expected columns</div>
            <div style={{ color: "#6B7280" }}>{TEMPLATE_HEADERS[template].join("  |  ")}</div>
            <ul className="mt-2 mb-2" style={{ paddingLeft: "18px", color: "#6B7280" }}>
              {TEMPLATE_HINTS[template].map((hint) => (
                <li key={hint}>{hint}</li>
              ))}
            </ul>
            <button className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1" onClick={() => downloadTemplate(template)}>
              <MdOutlineFileDownload size={16} /> Download template
            </button>
          </div>
        </div>
      ) : (
        <div>
          <div className="d-flex flex-wrap gap-2 mb-3 align-items-center" style={{ fontSize: "13px" }}>
            <span style={{ color: "#374151" }}>
              <strong>{fileName}</strong> &middot; {counts.total} rows
            </span>
            {counts.firstDate && (
              <span style={badgeStyle("#E0E7FF", "#3730A3")}>
                Job dates: {formatDateHyphenated(counts.firstDate)}
                {counts.lastDate !== counts.firstDate && ` to ${formatDateHyphenated(counts.lastDate)}`}
              </span>
            )}
            <span style={badgeStyle("#DCFCE7", "#166534")}>{counts.ready} ready</span>
            {counts.dups > 0 && <span style={badgeStyle("#FEF3C7", "#92400E")}>{counts.dups} duplicate{counts.dups === 1 ? "" : "s"}</span>}
            {counts.errors > 0 && <span style={badgeStyle("#FEE2E2", "#991B1B")}>{counts.errors} with errors</span>}
          </div>

          {counts.dups > 0 && (
            <div className="alert alert-warning py-2 d-flex align-items-start gap-2" style={{ fontSize: "13px" }}>
              <MdWarningAmber size={18} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>
                Duplicates share a {template === JC_ALQURE ? "Vehicle number, Date and Description" : "VIN and Job Schedule Date"} with an existing entry (or an earlier row in this file). They are
                unchecked by default; tick a row to include it anyway.
              </span>
            </div>
          )}

          <div style={cardStyle}>
            <div style={{ overflow: "auto", maxHeight: "340px" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                  <tr>
                    <th style={{ ...thStyle, width: 40 }}>
                      <input type="checkbox" checked={allSelected} onChange={toggleAll} aria-label="Select all rows" />
                    </th>
                    {[SERIAL_HEADER[template], ...(templateColumns ? templateColumns.map((c) => c.label) : ["Job Date", "Trucking Date", "VIN", "Model", "Classification", "Services", "Net", "VAT %", "Total"]), "Status"].map((h) => (
                      <th key={h} style={thStyle}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const hasError = r.errors.length > 0;
                    const dup = dupReason[i];
                    return (
                      <tr key={i} style={{ background: hasError ? "#FEF2F2" : dup ? "#FFFBEB" : "transparent" }}>
                        <td style={tdStyle}>
                          <input
                            type="checkbox"
                            checked={selected.has(i)}
                            disabled={hasError}
                            onChange={() => toggleRow(i)}
                            aria-label={`Include row ${r.rowNumber}`}
                          />
                        </td>
                        <td style={tdStyle}>{r.srNo ?? r.rowNumber}</td>
                        {templateColumns ? (
                          templateColumns.map((c, ci) => (
                            <td key={c.label} style={{ ...tdStyle, ...(c.nowrap ? { whiteSpace: "nowrap" } : {}) }}>
                              {c.get(r)}
                              {ci === templateColumns.length - 1 && mismatchIcon(r)}
                            </td>
                          ))
                        ) : (
                          <>
                            <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>{formatDateHyphenated(r.job_schedule_date)}</td>
                            <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>{formatDateHyphenated(r.trucking_date)}</td>
                            <td style={tdStyle}>{r.vin_number || "—"}</td>
                            <td style={tdStyle}>{r.model || "—"}</td>
                            <td style={tdStyle}>{r.classification || "—"}</td>
                            <td style={tdStyle}>{describeServices(r)}</td>
                            <td style={tdStyle}>{r.net_amount.toFixed(2)}</td>
                            <td style={tdStyle}>{r.vat_pct}%</td>
                            <td style={tdStyle}>
                              {r.total_amount.toFixed(2)}
                              {mismatchIcon(r)}
                            </td>
                          </>
                        )}
                        <td style={tdStyle}>
                          {hasError ? (
                            <span style={{ color: "#991B1B", fontSize: "12px" }}>{r.errors.join("; ")}</span>
                          ) : dup ? (
                            <span style={badgeStyle("#FEF3C7", "#92400E")}>{DUP_LABEL[dup]}</span>
                          ) : (
                            <span style={badgeStyle("#DCFCE7", "#166534")}>Ready</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-4" style={{ ...cardStyle, padding: "16px" }}>
            <div className="fw-bold mb-2" style={{ fontSize: "14px", color: "#111827" }}>
              Generate an invoice for the uploaded rows now?
            </div>
            <div className="d-flex flex-column gap-2" style={{ fontSize: "14px", color: "#374151" }}>
              <label className="d-flex align-items-center gap-2" style={{ cursor: "pointer" }}>
                <input type="radio" name="generate-now" checked={generateNow} onChange={() => setGenerateNow(true)} />
                Yes, generate one invoice for these rows right after upload
              </label>
              <label className="d-flex align-items-center gap-2" style={{ cursor: "pointer" }}>
                <input type="radio" name="generate-now" checked={!generateNow} onChange={() => setGenerateNow(false)} />
                No, I will generate it later from the Expenses list
              </label>
            </div>
            {generateNow && (
              <div className="mt-3">
                <div className="mb-3">
                  <InvoiceDateField value={invoiceDate} onChange={setInvoiceDate} />
                </div>
                <CustomerPicker value={customer} onChange={setCustomer} />
              </div>
            )}
          </div>
        </div>
      )}
    </SlidePanel>
  );
}

export default UploadExpenseDrawer;
